'use client';
import { Button, Divider, LoadingOverlay } from '@mantine/core';
import { IconArrowNarrowRight } from '@tabler/icons-react';
import { usePasswordless } from 'amazon-cognito-passwordless-auth/react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { PropsWithChildren, useEffect, useState } from 'react';
import { useAlert, useStripToken } from '../hooks';
import {
  FeatureFlagContextProvider,
  LDFeatureFlagProvider,
} from '../providers';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
// import MicrosoftLogin from 'react-microsoft-login';
import { useValidateAccessCodeMutation } from '../api';

interface IProps extends PropsWithChildren {
  unathenticatedPaths?: string[];
}

const SignedIn = ({ children }: PropsWithChildren) => {
  useStripToken();
  return (
    <LDFeatureFlagProvider>
      <FeatureFlagContextProvider>{children}</FeatureFlagContextProvider>
    </LDFeatureFlagProvider>
  );
};


export const SignIn = ({ children, unathenticatedPaths = [] }: IProps) => {
  const setAlert = useAlert();
  const { requestSignInLink, signInStatus, signingInStatus } =
    usePasswordless();

  const pathname = usePathname();

  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [showEmailField, setShowEmailField] = useState(false);
  const [code, setCode] = useState<number>();
  const [validateCode, { isLoading }] = useValidateAccessCodeMutation();

  const googleClientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || '';
  // const microsoftClientId = process.env.NEXT_PUBLIC_MICROSOFT_CLIENT_ID || '';
  const requestingLink = signingInStatus === 'REQUESTING_SIGNIN_LINK';
  const linkSent = signingInStatus === 'SIGNIN_LINK_REQUESTED';
  const buttonText = 'Log in Without a Password';

  useEffect(() => {
    if (signingInStatus === 'SIGNIN_LINK_REQUESTED') {
      setAlert({
        severity: 'success',
        title: 'Magic Link',
        message:
          'Magic Link email has been sent! Please check your email for further login instructions',
        show: true,
        duration: 50000,
      });
    } else if (signingInStatus === 'SIGNIN_LINK_REQUEST_FAILED') {
      setAlert({
        severity: 'error',
        title: 'Magic Link',
        message: 'Something went wrong! Please try again',
        show: true,
      });
    }
  }, [signingInStatus, setAlert]);

  const getLoginLink = (
    idToken: string,
    provider: string,
    errorMessage: string,
    createNewUser?: boolean
  ) => {
    const authUrl = new URL(
      '/user/auth',
      process.env.NEXT_PUBLIC_API_BASE_URL!
    );
    fetch(authUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token: idToken,
        provider: provider,
        redirectUrl: window.location.href.split('#')[0],
        createNewUser: createNewUser,
      }),
    })
      .then((response) => {
        setVisible(false);
        if (response.ok) {
          response.json().then((data) => {
            window.location.replace(data.url);
            window.location.reload();
          });
        } else if (response.status === 404) {
          response.text().then((text) => {
            setEmail(text);
            open();
          });
        } else {
          setAlert({
            severity: 'error',
            title: 'Login Error',
            message: errorMessage,
            show: true,
          });
        }
      })
      .catch((error) => {
        setVisible(false);
        setAlert({
          severity: 'error',
          title: 'Login Error',
          message: errorMessage,
          show: true,
        });
      });
  };

  const handleLoginWithCode = async () => {
    try {
      const { url } = await validateCode({
        email,
        accessCode: `${code}`,
      }).unwrap();
      window.location.replace(url);
      window.location.reload();
    } catch (ex) {
      setAlert({
        severity: 'error',
        title: 'Failed to validate access code',
        message: 'Something went wrong! Please try again',
        show: true,
      });
    }
  };

  if (['CHECKING', 'REFRESHING_SIGN_IN'].includes(signInStatus)) {
    return (
      <div className="h-[calc(100vh-64px)] flex justify-center items-center">
        <div className="border-t-transparent border-solid animate-spin rounded-full border-primary-800 border-4 h-16 w-16"></div>
      </div>
    );
  }

  if (
    unathenticatedPaths.includes(pathname) &&
    ['NOT_SIGNED_IN'].includes(signInStatus)
  ) {
    return children;
  }

  if (signInStatus === 'SIGNED_IN') {
    return <SignedIn>{children}</SignedIn>;
  }

  return (
    <>
      <LoadingOverlay
        visible={visible}
        zIndex={1000}
        overlayProps={{ radius: 'sm', blur: 2 }}
      />
      <div className="flex gap-4 items-center h-[calc(100vh-100px)] content-center">
        <div className="flex-1 flex flex-col rounded-[2rem] bg-white p-6 gap-[150px] max-h-[calc(100vh-64px)]">
          <div className="flex justify-center items-center pt-11">
            <Image
              src={'/logo.svg'}
              width={328}
              height={67}
              className="max-w-[calc(25vw)]"
              alt="Logo"
            />
          </div>
          <div className="flex flex-col gap-4">
            <p className="text-text-primary font-bold text-2xl text-center">
              Login to ScaleUp Platform
            </p>

            {!showEmailField && (
              <>
                <span className="flex justify-center">
                  <GoogleOAuthProvider clientId={googleClientId}>
                    <GoogleLogin
                      type="standard"
                      text="signin_with"
                      shape="rectangular"
                      size="large"
                      width={215}
                      logo_alignment="left"
                      theme="filled_blue"
                      onSuccess={(credentialResponse) => {
                        setVisible(true);
                        getLoginLink(
                          credentialResponse.credential!,
                          'google',
                          'Google login failed!'
                        );
                      }}
                      onError={() => {
                        setAlert({
                          severity: 'error',
                          title: 'Google Login Error',
                          message: 'Google login failed!',
                          show: true,
                        });
                      }}
                    />
                  </GoogleOAuthProvider>
                </span>
                {/* <span className="flex justify-center">
                  <MicrosoftLogin
                    buttonTheme="dark"
                    clientId={microsoftClientId}
                    authCallback={(error, authData) => {
                      if (error) {
                        setAlert({
                          severity: 'error',
                          title: 'Microsoft Login Error',
                          message: 'Microsoft login failed!',
                          show: true,
                        });
                      } else {
                        setVisible(true);
                        getLoginLink(
                          authData.idToken,
                          'microsoft',
                          'Microsoft login failed!'
                        );
                      }
                    }}
                    children={undefined}
                  />
                </span> */}
                <Divider my="md" className="mt-10 mb-10" />

                <p className="text-text-secondary text-center font-xs">
                  or use your email address
                </p>
                <Button
                  variant="filled"
                  color="blue"
                  rightSection={<IconArrowNarrowRight />}
                  onClick={() => setShowEmailField(true)}
                >
                  {buttonText}
                </Button>
              </>
            )}
            {showEmailField && (
              <div className="flex flex-col gap-1">
                <label>Email</label>
                <input
                  type="email"
                  disabled={linkSent}
                  className="w-full bg-gray-100 rounded-2xl p-4 text-gray-700 focus:bg-none focus:border-none"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            )}
            {linkSent && showEmailField && (
              <div className="flex flex-col gap-1">
                <label>Access Code</label>
                <input
                  type="number"
                  className="w-full bg-gray-100 rounded-2xl p-4 text-gray-700 focus:bg-none focus:border-none"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(Number(e.target.value))}
                />
              </div>
            )}
            {!linkSent && showEmailField && (
              <Button
                variant="filled"
                color="blue"
                disabled={requestingLink || !email || linkSent}
                rightSection={<IconArrowNarrowRight />}
                loading={requestingLink}
                onClick={() =>
                  requestSignInLink({
                    username: email.toLowerCase().trim(),
                  })
                }
              >
                {buttonText}
              </Button>
            )}

            {linkSent && showEmailField && (
              <Button
                variant="filled"
                color="blue"
                disabled={!code}
                rightSection={<IconArrowNarrowRight />}
                loading={isLoading}
                onClick={handleLoginWithCode}
              >
                {buttonText}
              </Button>
            )}
            {showEmailField && (
              <>
                <Divider my="md" className="mt-10 mb-10" />

                <p className="text-text-secondary text-center font-xs">
                  or use social login
                </p>
                <Button
                  variant="filled"
                  color="blue"
                  rightSection={<IconArrowNarrowRight />}
                  onClick={() => setShowEmailField(false)}
                >
                  Login with Social Login
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="flex-1">
          <Image
            src={'/signin.png'}
            width={752}
            height={855}
            alt="Placeholder"
            className="max-h-[calc(100vh-64px)] m-auto"
          />
        </div>
      </div>
    </>
  );
};
